import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOrganization } from '@/hooks/useOrganization';
import { useToast } from '@/hooks/use-toast';
import { toast as sonnerToast } from 'sonner';
import { queryKeys } from '@/lib/query-keys';

export interface ContractorRate {
  id: string;
  organization_id: string;
  org_person_id: string;
  hourly_cost_rate: number;
  cost_currency: string;
  available_hours_per_week: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  // Joined data
  org_person?: {
    id: string;
    name: string;
    role_title: string | null;
    side_type: string;
    is_active: boolean;
  };
}

export interface ContractorRateInput {
  org_person_id: string;
  hourly_cost_rate: number;
  cost_currency?: string;
  available_hours_per_week?: number;
}

/**
 * Hook to fetch all contractor rates for the organization
 * Manager-only - RLS enforced
 */
export function useContractorRates() {
  const { organization, hasCapability } = useOrganization();
  const canViewFinancials = hasCapability('canViewFinancials');

  return useQuery({
    queryKey: queryKeys.budget.contractorRates(organization?.id),
    queryFn: async (): Promise<ContractorRate[]> => {
      if (!organization?.id) return [];

      const { data, error } = await supabase
        .from('contractor_rates')
        .select(`
          *,
          org_person:org_people(id, name, role_title, side_type, is_active)
        `)
        .eq('organization_id', organization.id)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('[useContractorRates] Error:', error);
        throw error;
      }

      return (data || []).map(item => ({
        ...item,
        org_person: item.org_person as ContractorRate['org_person'],
      }));
    },
    enabled: !!organization?.id && canViewFinancials,
  });
}

/**
 * Hook to get a single contractor rate by org_person_id
 */
export function useContractorRate(orgPersonId: string | undefined) {
  const { organization, hasCapability } = useOrganization();
  const canViewFinancials = hasCapability('canViewFinancials');

  return useQuery({
    queryKey: queryKeys.budget.contractorRate(orgPersonId),
    queryFn: async (): Promise<ContractorRate | null> => {
      if (!organization?.id || !orgPersonId) return null;

      const { data, error } = await supabase
        .from('contractor_rates')
        .select(`
          *,
          org_person:org_people(id, name, role_title, side_type, is_active)
        `)
        .eq('org_person_id', orgPersonId)
        .maybeSingle();

      if (error) {
        console.error('[useContractorRate] Error:', error);
        throw error;
      }

      if (!data) return null;

      return {
        ...data,
        org_person: data.org_person as ContractorRate['org_person'],
      };
    },
    enabled: !!organization?.id && !!orgPersonId && canViewFinancials,
  });
}

/**
 * Hook to create or update contractor rate
 * Automatically recalculates COGS and updates budget metrics when rates change
 */
export function useUpsertContractorRate() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { organization } = useOrganization();

  return useMutation({
    mutationFn: async (input: ContractorRateInput) => {
      if (!organization?.id) throw new Error('No organization');

      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('contractor_rates')
        .upsert({
          organization_id: organization.id,
          org_person_id: input.org_person_id,
          hourly_cost_rate: input.hourly_cost_rate,
          cost_currency: input.cost_currency || 'USD',
          available_hours_per_week: input.available_hours_per_week || 40,
          created_by: user.user.id,
        }, {
          onConflict: 'org_person_id',
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      // Invalidate contractor rate queries
      queryClient.invalidateQueries({ queryKey: queryKeys.budget.contractorRates() });
      queryClient.invalidateQueries({ queryKey: queryKeys.budget.contractorRate() });

      toast({
        title: 'Rate saved',
        description: 'Contractor rate has been updated. Recalculating COGS...',
      });

      // Auto-recalculate COGS by re-running populate_budget_actuals_from_everhour
      if (organization?.id) {
        const currentYear = new Date().getFullYear();
        try {
          await supabase.rpc('populate_budget_actuals_from_everhour', {
            p_org_id: organization.id,
            p_fiscal_year: currentYear,
          });

          // Invalidate budget queries to refresh UI
          queryClient.invalidateQueries({ queryKey: queryKeys.budget.dashboard });
          queryClient.invalidateQueries({ queryKey: queryKeys.budget.metrics });
          queryClient.invalidateQueries({ queryKey: queryKeys.budget.yearSummary });
          queryClient.invalidateQueries({ queryKey: queryKeys.budget.actuals });
          queryClient.invalidateQueries({ queryKey: queryKeys.budget.rateAnalysis });

          sonnerToast.success('COGS and margins recalculated');
        } catch (err) {
          console.error('[useUpsertContractorRate] Failed to recalculate COGS:', err);
          sonnerToast.error('Rate saved, but failed to recalculate COGS automatically');
        }
      }
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to save rate',
        description: error.message || 'Something went wrong',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook to delete contractor rate
 * Automatically recalculates COGS and updates budget metrics when rates are removed
 */
export function useDeleteContractorRate() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { organization } = useOrganization();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('contractor_rates')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.budget.contractorRates(organization?.id) });
      toast({
        title: 'Rate removed',
        description: 'Contractor rate has been removed. Recalculating COGS...',
      });

      // Auto-recalculate COGS by re-running populate_budget_actuals_from_everhour
      if (organization?.id) {
        const currentYear = new Date().getFullYear();
        try {
          await supabase.rpc('populate_budget_actuals_from_everhour', {
            p_org_id: organization.id,
            p_fiscal_year: currentYear,
          });

          // Invalidate budget queries to refresh UI
          queryClient.invalidateQueries({ queryKey: queryKeys.budget.dashboard });
          queryClient.invalidateQueries({ queryKey: queryKeys.budget.metrics });
          queryClient.invalidateQueries({ queryKey: queryKeys.budget.yearSummary });
          queryClient.invalidateQueries({ queryKey: queryKeys.budget.actuals });
          queryClient.invalidateQueries({ queryKey: queryKeys.budget.rateAnalysis });

          sonnerToast.success('COGS and margins recalculated');
        } catch (err) {
          console.error('[useDeleteContractorRate] Failed to recalculate COGS:', err);
          sonnerToast.error('Rate removed, but failed to recalculate COGS automatically');
        }
      }
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to remove rate',
        description: error.message || 'Something went wrong',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook to bulk update contractor rates
 */
export function useBulkUpdateContractorRates() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { organization } = useOrganization();

  return useMutation({
    mutationFn: async (rates: ContractorRateInput[]) => {
      if (!organization?.id) throw new Error('No organization');

      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('Not authenticated');

      const upsertData = rates.map(rate => ({
        organization_id: organization.id,
        org_person_id: rate.org_person_id,
        hourly_cost_rate: rate.hourly_cost_rate,
        cost_currency: rate.cost_currency || 'USD',
        available_hours_per_week: rate.available_hours_per_week || 40,
        created_by: user.user.id,
      }));

      const { data, error } = await supabase
        .from('contractor_rates')
        .upsert(upsertData, {
          onConflict: 'org_person_id',
        })
        .select();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.budget.contractorRates(organization?.id) });
      toast({
        title: 'Rates saved',
        description: 'All contractor rates have been updated.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to save rates',
        description: error.message || 'Something went wrong',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Calculate blended contractor rate from rates data
 */
export function calculateBlendedRate(rates: ContractorRate[]): number {
  const activeRates = rates.filter(r => r.org_person?.is_active);
  if (activeRates.length === 0) return 0;

  let totalWeightedRate = 0;
  let totalHours = 0;

  for (const rate of activeRates) {
    const hours = rate.available_hours_per_week;
    totalWeightedRate += rate.hourly_cost_rate * hours;
    totalHours += hours;
  }

  return totalHours === 0 ? 0 : totalWeightedRate / totalHours;
}
